import { User } from '@auth0/auth0-react';
import { IFlags, IFlagsmithFeature, useFlagsmith } from '@flagsmith/flagsmith/react';
import { useEffect } from 'react';
import { proxy } from 'valtio';

export type FlagsmithRole = 'deepup' | 'customer';

export type FlagsmithUser = {
  email?: string;
};

export type FlagsmithIdentity = {
  identifier: string;
  traits: {
    role: FlagsmithRole;
  };
};

export const getFlagsmithIdentity = (user?: FlagsmithUser | null): FlagsmithIdentity | null => {
  const email = user?.email?.toLowerCase();
  const domain = email?.split('@')[1]?.toLowerCase();

  if (!email || !domain) {
    return null;
  }

  const isDeepUp = domain === 'deepup.de' || domain === 'deepup.ai';

  return {
    identifier: isDeepUp ? email : domain,
    traits: {
      role: isDeepUp ? 'deepup' : 'customer',
    },
  };
};

type FlagsmithState = {
  flags: IFlags | null;
};

const flagsmithState = proxy<FlagsmithState>({
  flags: null,
});

export const setFlags = (flags: IFlags) => {
  flagsmithState.flags = flags;
};

export const getFlags = () => {
  if (!flagsmithState.flags) {
    throw new Error('Flagsmith not initialized');
  }

  return flagsmithState.flags;
};

export const getIsFeatureEnabled = (
  flag: IFlagsmithFeature | undefined,
  user: User | null | undefined,
) => {
  const enabled = flag && flag.enabled;

  if (!enabled) {
    return false;
  }

  if (!flag.value) {
    return enabled;
  }

  const userEmail = user?.email?.toLowerCase();
  if (!userEmail) {
    return enabled;
  }

  try {
    const userEmails = JSON.parse(String(flag.value)) as string[];
    const lowercasedEmails = userEmails.map((email) => email.toLowerCase());
    if (!lowercasedEmails.includes(userEmail)) {
      return false;
    }
  } catch {
    return enabled;
  }

  return enabled;
};

export const useSetupFlagsmith = () => {
  const flagsmith = useFlagsmith();
  const flags = flagsmith.getAllFlags();

  useEffect(() => {
    setFlags(flags);
  }, [flags]);
};
